import axios from 'axios';
import type { Optional } from '@gov-nx/core/types/helpers.types';
import type { AuthState } from '@gov-nx/store/auth/store.types';
import type { ConfigurationState } from '@gov-nx/store/configuration/store.types';
import { getPoServerUrl } from '@gov-nx/store/configuration/utils';
import { getStore } from '@gov-nx/store/constants';
import type { UserState } from '@gov-nx/store/user/store.types';
import { isLanguageSetting } from '@gov-nx/store/user/utils';
import type { RezaOpravneniDto } from './generated/reza.types';
import { logger } from './logger';

const LANG_GET_URL = '/api/v1/be/nastaveni/LANG';
const LANG_POST_URL = '/api/v1/be/nastaveni';
const LANG_PAYLOAD_KEY = 'LANG';

const instance = axios.create({
	headers: {
		'Content-Type': 'application/json',
	},
	paramsSerializer: {
		indexes: null,
	},
});

export const useAxiosInstance = () =>
	// TODO: Setup request interceptors in here

	({ axiosInstance: instance });

let activeSwitcherReza: RezaOpravneniDto | null = null;

export const setActiveSwitcherRezaForAxios = (
	reza: RezaOpravneniDto | null
) => {
	activeSwitcherReza = reza;
};

const setup = () => {
	instance.interceptors.request.use((config) => {
		const userStore = getStore<Optional<UserState>>('user')?.getState();
		const authStore = getStore<Optional<AuthState>>('auth')?.getState();
		const configurationStore =
			getStore<Optional<ConfigurationState>>('configuration')?.getState();

		const language = userStore?.settings.find(isLanguageSetting)?.value;
		if (language) {
			config.headers['Accept-Language'] = language;
		}

		if (authStore?.token) {
			config.headers.Authorization = 'Bearer ' + authStore.token;
		}

		const isLangGetRequest =
			config.url === LANG_GET_URL && config.method === 'get';

		const isLangPostRequest =
			config.url === LANG_POST_URL &&
			config.method === 'post' &&
			config.data?.klic === LANG_PAYLOAD_KEY;

		const isLanguageSettingsRequest = isLangGetRequest || isLangPostRequest;

		// Language preference is user-global, not REZA-scoped —
		// sending x-okz would scope the setting to a single REZA incorrectly.
		if (!isLanguageSettingsRequest) {
			config.headers['x-okz'] =
				config.headers['x-okz'] ??
				activeSwitcherReza?.kod ??
				authStore?.activeReza?.kod ??
				'';
		}

		logger('request', [
			authStore?.token
				? `${authStore.token.substring(0, 16)} ... ${authStore.token.substring(
						authStore.token.length - 16,
						authStore.token.length - 1
					)}`
				: 'NO-TOKEN',
			config.url,
			JSON.stringify(config.params),
		]);

		const serverUrl = getPoServerUrl(configurationStore?.configuration);
		if (serverUrl) {
			config.baseURL = serverUrl;
		}

		return config;
	});
};

/**
 *
 * @deprecated calling setup() should not be necessary
 */
export const setupApiAxiosInterceptors = setup;
